<template>
  <Teleport to="body">
    <div v-if="open && lamella" class="lamella-modal" role="dialog" aria-modal="true">
      <button class="lamella-modal__scrim" type="button" aria-label="Close detail" @click="$emit('close')"></button>
      <section class="lamella-modal__panel">
        <header class="lamella-modal__header">
          <div>
            <p class="lamella-modal__eyebrow">{{ gridName || "Detached grid record" }}</p>
            <h3 class="lamella-modal__title">{{ lamella.name }}</h3>
            <p class="lamella-modal__subtitle">
              {{ lamella.currentStage || "No active stage" }}
            </p>
          </div>
          <div class="lamella-modal__header-actions">
            <LamellaStatusBadge :status="lamella.status" />
            <button class="lamella-modal__close" type="button" @click="$emit('close')">Close</button>
          </div>
        </header>

        <nav class="lamella-modal__tabs" aria-label="Lamella details">
          <button
            v-for="tab in tabs"
            :key="tab.id"
            type="button"
            class="lamella-modal__tab"
            :class="{ 'lamella-modal__tab--active': activeTab === tab.id }"
            @click="activeTab = tab.id"
          >
            {{ tab.label }}
          </button>
        </nav>

        <div class="lamella-modal__body">
          <section v-if="activeTab === 'overview'" class="lamella-modal__grid">
            <article class="lamella-panel">
              <h4>Session</h4>
              <dl>
                <div><dt>Status</dt><dd>{{ lamella.status || "pending" }}</dd></div>
                <div><dt>Stage</dt><dd>{{ lamella.currentStage || "N/A" }}</dd></div>
                <div><dt>Images</dt><dd>{{ lamella.imageRefs?.length || 0 }}</dd></div>
                <div><dt>Observed</dt><dd>{{ formatDate(lamella.lastObservedAt || lamella.dateModified) }}</dd></div>
              </dl>
            </article>

            <article class="lamella-panel">
              <h4>External identity</h4>
              <dl>
                <div><dt>Source</dt><dd>{{ lamella.externalSource || "Manual" }}</dd></div>
                <div><dt>Experiment</dt><dd>{{ lamella.externalExperimentId || "N/A" }}</dd></div>
                <div><dt>Lamella ID</dt><dd>{{ lamella.externalLamellaId || "N/A" }}</dd></div>
                <div><dt>Fingerprint</dt><dd class="lamella-panel__mono">{{ lamella.externalFingerprint || "N/A" }}</dd></div>
              </dl>
            </article>

            <article class="lamella-panel lamella-panel--wide">
              <h4>Notes</h4>
              <p>{{ lamella.notes || "No operator notes recorded." }}</p>
            </article>
          </section>

          <section v-else-if="activeTab === 'images'" class="lamella-images">
            <form
              v-if="!readonly"
              class="lamella-images__upload"
              @submit.prevent="submitImageUpload"
            >
              <div class="lamella-images__upload-header">
                <div>
                  <p class="lamella-images__eyebrow">Upload</p>
                  <h4>Add lamella image</h4>
                </div>
                <button type="submit" class="lamella-images__submit" :disabled="isUploading || !selectedFile">
                  {{ isUploading ? "Uploading..." : "Upload image" }}
                </button>
              </div>
              <div class="lamella-images__upload-grid">
                <label class="lamella-images__field lamella-images__field--file">
                  <span>Image file</span>
                  <input type="file" accept="image/*" @change="handleFileSelection" />
                </label>
                <label class="lamella-images__field">
                  <span>Stage</span>
                  <input v-model="uploadStage" type="text" placeholder="Polish, rough mill, atlas..." />
                </label>
                <label class="lamella-images__field">
                  <span>Beam type</span>
                  <input v-model="uploadBeamType" type="text" placeholder="SEM, FIB, cryo-ET..." />
                </label>
              </div>
              <div class="lamella-images__upload-meta">
                <span>{{ selectedFile?.name || "No file selected" }}</span>
                <span v-if="uploadError" class="lamella-images__error">{{ uploadError }}</span>
                <span v-else-if="uploadSuccess" class="lamella-images__success">{{ uploadSuccess }}</span>
              </div>
            </form>
            <div v-if="groupedImages.length" class="lamella-images__groups">
              <article v-for="group in groupedImages" :key="group.label" class="lamella-panel">
                <div class="lamella-images__group-header">
                  <h4>{{ group.label }}</h4>
                  <span>{{ group.images.length }} image{{ group.images.length === 1 ? "" : "s" }}</span>
                </div>
                <div class="lamella-images__grid">
                  <a
                    v-for="image in group.images"
                    :key="image.storageKey"
                    class="lamella-images__tile"
                    :href="image.url"
                    target="_blank"
                    rel="noreferrer"
                  >
                    <img :src="image.url" :alt="`${lamella.name} ${group.label}`" loading="lazy" />
                    <div>
                      <strong>{{ image.beamType || "image" }}</strong>
                      <span>{{ formatDate(image.timestamp) }}</span>
                    </div>
                    <button
                      v-if="!readonly"
                      type="button"
                      class="lamella-images__delete"
                      @click.prevent.stop="deleteImage(image.storageKey)"
                    >
                      Delete
                    </button>
                  </a>
                </div>
              </article>
            </div>
            <div v-else class="lamella-modal__empty">No uploaded images for this lamella.</div>
          </section>

          <section v-else-if="activeTab === 'parameters'" class="lamella-modal__grid">
            <article class="lamella-panel">
              <h4>Stage coordinates</h4>
              <pre>{{ prettyValue(lamella.site) }}</pre>
            </article>
            <article class="lamella-panel">
              <h4>Protocol</h4>
              <pre>{{ prettyValue(lamella.protocol) }}</pre>
            </article>
            <article class="lamella-panel lamella-panel--wide">
              <h4>Source metadata</h4>
              <pre>{{ prettyValue(lamella.sourceMetadata) }}</pre>
            </article>
          </section>

          <section v-else class="lamella-timeline">
            <article v-for="event in timelineEvents" :key="`${event.label}-${event.value}`" class="lamella-timeline__event">
              <div class="lamella-timeline__dot"></div>
              <div>
                <div class="lamella-timeline__label">{{ event.label }}</div>
                <div class="lamella-timeline__value">{{ event.value }}</div>
              </div>
            </article>
            <div v-if="!timelineEvents.length" class="lamella-modal__empty">No timeline events captured yet.</div>
          </section>
        </div>
      </section>
    </div>
  </Teleport>
</template>

<script setup>
import { computed, ref, watch } from "vue";
import LamellaStatusBadge from "./LamellaStatusBadge.vue";

defineEmits(["close"]);

const props = defineProps({
  open: {
    type: Boolean,
    default: false,
  },
  lamella: {
    type: Object,
    default: null,
  },
  gridName: {
    type: String,
    default: null,
  },
  readonly: {
    type: Boolean,
    default: false,
  },
});

const tabs = [
  { id: "overview", label: "Overview" },
  { id: "images", label: "Images" },
  { id: "parameters", label: "Parameters" },
  { id: "timeline", label: "Timeline" },
];

const activeTab = ref("overview");
const selectedFile = ref(null);
const uploadStage = ref("");
const uploadBeamType = ref("");
const isUploading = ref(false);
const uploadError = ref("");
const uploadSuccess = ref("");
const deletingStorageKeys = ref([]);

watch(
  () => props.lamella?.id,
  () => {
    activeTab.value = "overview";
    selectedFile.value = null;
    uploadStage.value = "";
    uploadBeamType.value = "";
    uploadError.value = "";
    uploadSuccess.value = "";
    deletingStorageKeys.value = [];
  }
);

const groupedImages = computed(() => {
  const groups = new Map();
  for (const image of props.lamella?.imageRefs || []) {
    const label = image.stage || "Unsorted";
    const current = groups.get(label) || [];
    current.push(image);
    groups.set(label, current);
  }
  return Array.from(groups.entries()).map(([label, images]) => ({ label, images }));
});

const timelineEvents = computed(() => {
  const lamella = props.lamella;
  if (!lamella) return [];
  return [
    { label: "Created", value: formatDate(lamella.dateCreated) },
    lamella.startedAt ? { label: "Started", value: formatDate(lamella.startedAt) } : null,
    lamella.lastObservedAt ? { label: "Last observed", value: formatDate(lamella.lastObservedAt) } : null,
    lamella.completedAt ? { label: "Completed", value: formatDate(lamella.completedAt) } : null,
    lamella.externalUpdatedAt ? { label: "External update", value: formatDate(lamella.externalUpdatedAt) } : null,
  ].filter(Boolean);
});

function formatDate(value) {
  if (!value) return "N/A";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);
  return parsed.toLocaleString();
}

function prettyValue(value) {
  if (!value) return "No data recorded.";
  return JSON.stringify(value, null, 2);
}

function handleFileSelection(event) {
  const input = event.target;
  selectedFile.value = input?.files?.[0] || null;
  uploadError.value = "";
  uploadSuccess.value = "";
}

function getApiHost() {
  return import.meta.env.VITE_APP_HOST || window.location.origin;
}

function getAuthHeaders() {
  const jwt = localStorage.getItem("subangstrom_auth_jwt") || localStorage.getItem("jwt");
  return jwt ? { Authorization: `Bearer ${jwt}` } : {};
}

async function submitImageUpload() {
  if (!props.lamella?.id || !selectedFile.value || isUploading.value) return;
  isUploading.value = true;
  uploadError.value = "";
  uploadSuccess.value = "";

  const formData = new FormData();
  formData.append("image", selectedFile.value);
  if (uploadStage.value.trim()) formData.append("stage", uploadStage.value.trim());
  if (uploadBeamType.value.trim()) formData.append("beamType", uploadBeamType.value.trim());
  formData.append("timestamp", new Date().toISOString());

  try {
    const response = await fetch(
      `${getApiHost()}/api/v1/users/lamellae/${props.lamella.id}/images`,
      {
        method: "POST",
        credentials: "include",
        headers: getAuthHeaders(),
        body: formData,
      }
    );
    const data = await response.json();
    if (!response.ok || data.status !== "success") {
      throw new Error(data.message || "Image upload failed");
    }
    uploadSuccess.value = "Image uploaded.";
    selectedFile.value = null;
    uploadStage.value = "";
    uploadBeamType.value = "";
  } catch (error) {
    uploadError.value = error instanceof Error ? error.message : "Image upload failed";
  } finally {
    isUploading.value = false;
  }
}

async function deleteImage(storageKey) {
  if (!props.lamella?.id || !storageKey || deletingStorageKeys.value.includes(storageKey)) return;
  const confirmed = window.confirm("Delete this lamella image permanently?");
  if (!confirmed) return;
  deletingStorageKeys.value = [...deletingStorageKeys.value, storageKey];
  uploadError.value = "";
  uploadSuccess.value = "";

  try {
    const response = await fetch(
      `${getApiHost()}/api/v1/users/lamellae/${props.lamella.id}/images/${encodeURIComponent(storageKey)}`,
      {
        method: "DELETE",
        credentials: "include",
        headers: getAuthHeaders(),
      }
    );
    const data = await response.json();
    if (!response.ok || data.status !== "success") {
      throw new Error(data.message || "Image deletion failed");
    }
    uploadSuccess.value = "Image deleted.";
  } catch (error) {
    uploadError.value = error instanceof Error ? error.message : "Image deletion failed";
  } finally {
    deletingStorageKeys.value = deletingStorageKeys.value.filter((key) => key !== storageKey);
  }
}
</script>

<style scoped>
.lamella-modal {
  position: fixed;
  inset: 0;
  z-index: 160;
}
.lamella-modal__scrim {
  position: absolute;
  inset: 0;
  border: none;
  background: rgba(15, 23, 42, 0.7);
  backdrop-filter: blur(6px);
}
.lamella-modal__panel {
  position: absolute;
  inset: 1rem;
  background:
    radial-gradient(circle at top left, rgba(14, 165, 233, 0.12), transparent 28rem),
    linear-gradient(180deg, #f8fafc 0%, #eef6ff 100%);
  border: 1px solid rgba(148, 163, 184, 0.45);
  border-radius: 1.5rem;
  box-shadow: 0 28px 80px rgba(15, 23, 42, 0.35);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.lamella-modal__header,
.lamella-modal__tabs {
  padding: 1rem 1.25rem;
  border-bottom: 1px solid rgba(148, 163, 184, 0.25);
}
.lamella-modal__header {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
}
.lamella-modal__eyebrow {
  margin: 0;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  font-size: 0.7rem;
  color: #0f766e;
}
.lamella-modal__title {
  margin: 0.2rem 0 0;
  font-size: 1.5rem;
  color: #0f172a;
}
.lamella-modal__subtitle {
  margin: 0.25rem 0 0;
  color: #475569;
}
.lamella-modal__header-actions {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
}
.lamella-modal__close {
  border: 1px solid #94a3b8;
  background: white;
  border-radius: 999px;
  padding: 0.45rem 0.9rem;
  font-weight: 700;
}
.lamella-modal__tabs {
  display: flex;
  gap: 0.65rem;
  flex-wrap: wrap;
}
.lamella-modal__tab {
  border: none;
  background: rgba(226, 232, 240, 0.9);
  color: #334155;
  border-radius: 999px;
  padding: 0.45rem 0.9rem;
  font-weight: 700;
}
.lamella-modal__tab--active {
  background: #0f766e;
  color: white;
}
.lamella-modal__body {
  overflow: auto;
  padding: 1rem 1.25rem 1.25rem;
}
.lamella-modal__grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1rem;
}
.lamella-panel {
  background: rgba(255, 255, 255, 0.86);
  border: 1px solid rgba(148, 163, 184, 0.25);
  border-radius: 1.1rem;
  padding: 1rem;
}
.lamella-panel--wide {
  grid-column: 1 / -1;
}
.lamella-panel h4 {
  margin: 0 0 0.75rem;
  font-size: 0.95rem;
  color: #0f172a;
}
.lamella-panel dl {
  display: grid;
  gap: 0.75rem;
  margin: 0;
}
.lamella-panel dl div {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  border-bottom: 1px dashed rgba(148, 163, 184, 0.28);
  padding-bottom: 0.4rem;
}
.lamella-panel dt {
  color: #64748b;
}
.lamella-panel dd {
  margin: 0;
  text-align: right;
  color: #0f172a;
  font-weight: 600;
}
.lamella-panel__mono {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 0.75rem;
  word-break: break-all;
}
.lamella-panel pre {
  margin: 0;
  white-space: pre-wrap;
  word-break: break-word;
  font-size: 0.78rem;
  line-height: 1.5;
  color: #0f172a;
}
.lamella-images__groups {
  display: grid;
  gap: 1rem;
}
.lamella-images__upload {
  margin-bottom: 1rem;
  padding: 1rem;
  border-radius: 1rem;
  border: 1px solid rgba(14, 165, 233, 0.18);
  background: linear-gradient(180deg, rgba(255,255,255,0.92), rgba(239,246,255,0.9));
}
.lamella-images__upload-header {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  align-items: center;
  margin-bottom: 0.85rem;
}
.lamella-images__eyebrow {
  margin: 0 0 0.2rem;
  text-transform: uppercase;
  letter-spacing: 0.14em;
  font-size: 0.68rem;
  color: #0f766e;
  font-weight: 800;
}
.lamella-images__upload-header h4 {
  margin: 0;
  color: #0f172a;
}
.lamella-images__upload-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.75rem;
}
.lamella-images__field {
  display: grid;
  gap: 0.35rem;
}
.lamella-images__field--file {
  grid-column: span 1;
}
.lamella-images__field span {
  color: #64748b;
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  font-weight: 700;
}
.lamella-images__field input {
  width: 100%;
  border: 1px solid rgba(148, 163, 184, 0.4);
  border-radius: 0.85rem;
  background: rgba(255, 255, 255, 0.98);
  padding: 0.72rem 0.85rem;
}
.lamella-images__submit {
  border: none;
  border-radius: 999px;
  padding: 0.7rem 1rem;
  background: linear-gradient(135deg, #0f766e, #0369a1);
  color: white;
  font-weight: 800;
}
.lamella-images__submit:disabled {
  opacity: 0.6;
}
.lamella-images__upload-meta {
  display: flex;
  justify-content: space-between;
  gap: 0.75rem;
  margin-top: 0.75rem;
  color: #64748b;
  font-size: 0.8rem;
}
.lamella-images__error {
  color: #b91c1c;
}
.lamella-images__success {
  color: #047857;
}
.lamella-images__group-header {
  display: flex;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 0.85rem;
}
.lamella-images__group-header h4 {
  margin: 0;
}
.lamella-images__group-header span {
  color: #64748b;
  font-size: 0.82rem;
}
.lamella-images__grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(11rem, 1fr));
  gap: 0.75rem;
}
.lamella-images__tile {
  display: grid;
  gap: 0.55rem;
  text-decoration: none;
  color: inherit;
  position: relative;
}
.lamella-images__tile img {
  width: 100%;
  aspect-ratio: 4 / 3;
  object-fit: cover;
  border-radius: 0.9rem;
  background: #e2e8f0;
}
.lamella-images__tile strong,
.lamella-images__tile span {
  display: block;
}
.lamella-images__tile span {
  color: #64748b;
  font-size: 0.78rem;
}
.lamella-images__delete {
  position: absolute;
  top: 0.55rem;
  right: 0.55rem;
  border: none;
  border-radius: 999px;
  padding: 0.35rem 0.65rem;
  background: rgba(127, 29, 29, 0.92);
  color: white;
  font-size: 0.72rem;
  font-weight: 700;
  box-shadow: 0 8px 18px rgba(15, 23, 42, 0.18);
}
.lamella-timeline {
  display: grid;
  gap: 0.9rem;
}
.lamella-timeline__event {
  display: flex;
  gap: 0.9rem;
  align-items: flex-start;
  background: rgba(255, 255, 255, 0.8);
  border: 1px solid rgba(148, 163, 184, 0.25);
  border-radius: 1rem;
  padding: 0.9rem 1rem;
}
.lamella-timeline__dot {
  width: 0.8rem;
  height: 0.8rem;
  border-radius: 999px;
  margin-top: 0.3rem;
  background: linear-gradient(135deg, #0f766e, #38bdf8);
  box-shadow: 0 0 0 4px rgba(15, 118, 110, 0.12);
}
.lamella-timeline__label {
  font-weight: 700;
  color: #0f172a;
}
.lamella-timeline__value {
  color: #475569;
}
.lamella-modal__empty {
  border: 1px dashed rgba(148, 163, 184, 0.6);
  border-radius: 1rem;
  padding: 1rem;
  color: #64748b;
  background: rgba(255, 255, 255, 0.6);
}
@media (max-width: 900px) {
  .lamella-modal__panel {
    inset: 0;
    border-radius: 0;
  }
  .lamella-modal__grid {
    grid-template-columns: 1fr;
  }
  .lamella-modal__header {
    flex-direction: column;
  }
  .lamella-modal__header-actions {
    justify-content: space-between;
  }
  .lamella-images__upload-header,
  .lamella-images__upload-meta {
    flex-direction: column;
    align-items: stretch;
  }
  .lamella-images__upload-grid {
    grid-template-columns: 1fr;
  }
}
</style>
